import {Injectable, inject} from '@angular/core';
import {forkJoin, Observable, of, Subscriber, takeUntil, Subject, map, catchError, switchMap, from} from 'rxjs';
import {Data, IDataService, DataNode} from 'filemanager-explorer';
import {ExplorerEntity} from '@interfaces/explorer-entity';
import {FilemanagerApiService} from '@services/api/filemanager/filemanager-api.service';
import {ApiResponse} from '@interfaces/response';
import {MatDialog} from '@angular/material/dialog';
import {environment} from '@environment/environment';
import {ModalsDialogComponent} from '@components/modals/modals-dialog/modals-dialog.component';
import {ModalData} from '@interfaces/modal-data';
import {ModalsFileViewComponent} from '@components/modals/modals-file-view/modals-file-view.component';
import {ModalsSendFileComponent} from '@components/modals/modals-send-file/modals-send-file.component';
import {LoaderService} from '@services/loader/loader.service';
import {ModalsCutPdfComponent} from '@components/modals/modals-cut-pdf/modals-cut-pdf.component';
import { DataService } from '@projects/filemanager-explorer/src/public-api';


@Injectable({
  providedIn: 'root'
})
export class FilemanagerService extends DataService implements IDataService<ExplorerEntity>  {

  private fileManagerService = inject(FilemanagerApiService);
  private loadingService: LoaderService = inject(LoaderService);
  private matDialog = inject(MatDialog);

  private destroy$ = new Subject<void>();

  constructor() {
    super()
  }

  sendFile(data: ExplorerEntity): Observable<any> {

    this.fileManagerService.getFileBytes(data.uuid).subscribe({
      next: (response) => {
        let data_response = {
          data: response,
          name: data.name,
          uuid: data.uuid,
        };
        if (data['content_type'] === "application/pdf") {
          this.matDialog.open(ModalsCutPdfComponent, {
            data: data_response,
            width: 'auto',
            minHeight: '60vh',
            maxHeight: '90vh',
            maxWidth: '90vw',
          });
        } else {
          this.matDialog.open(ModalsSendFileComponent, {
            data: data_response,
            width: 'auto',
            minHeight: '60vh',
            maxHeight: '90vh',
            maxWidth: '90vw',
          });
        }
      },
      error: (err) => {
      }
    });
    return of({});
  }

  editFile(data: ExplorerEntity): Observable<any> {

    this.fileManagerService.getFileBytes(data.uuid).subscribe({
      next: (response) => {
        let data_response = {
          data: response,
          name: data.name,
          uuid: data.uuid,
          edit: true,
          originalFile: data,
          showClose: true
        };
        if (data['content_type'] === "application/pdf") {
          this.matDialog.open(ModalsCutPdfComponent, {
            data: data_response,
            width: 'auto',
            minHeight: '60vh',
            maxHeight: '90vh',
            maxWidth: '90vw',
            // Niente layer/backdrop sotto la modale: la pagina resta interattiva
            // e si possono usare in contemporanea la chat e il documento aperto.
            // Si chiude solo dal pulsante di chiusura (non dal click esterno).
            hasBackdrop: false,
            disableClose: true,
          });
        } else {
          this.matDialog.open(ModalsSendFileComponent, {
            data: data_response,
            width: 'auto',
            minHeight: '60vh',
            maxHeight: '90vh',
            maxWidth: '90vw',
            // Niente layer/backdrop sotto la modale: la pagina resta interattiva
            // e si possono usare in contemporanea la chat e il documento aperto.
            // Si chiude solo dal pulsante di chiusura (non dal click esterno).
            hasBackdrop: false,
            disableClose: true,
          });
        }
      },
      error: (err) => {
      }
    });
    return of({});
  }

  sendToRag(data: ExplorerEntity): Observable<any> {
    return this.fileManagerService.sendToRag(data.uuid as string).pipe(
      map((response) => response),
      catchError(() => of(null))
    );
  }

  removeFromRag(data: ExplorerEntity): Observable<any> {
    return this.fileManagerService.removeFromRag(data.uuid as string).pipe(
      map((response) => response),
      catchError(() => of(null))
    );
  }

  sendFileRaw(data: ExplorerEntity): Observable<any> {
    return this.fileManagerService.getFileBytes(data.uuid).pipe(map((response) => {
      return {
        data: response,
        name: data.name,
        uuid: data.uuid,
      }
    }))
  }

  viewFile(data: ExplorerEntity): Observable<any> {
    this.fileManagerService.getFileBytes(data.uuid).subscribe({
      next: (response) => {
        let data_response = {
          data: response,
          name: data.name
        };
        this.matDialog.open(ModalsFileViewComponent, {
          data: data_response,
          width: '70vw',
          minHeight: '60vh',
          maxHeight: '90vh',
          maxWidth: '90vw',
          // Niente layer/backdrop sotto la modale: così la pagina resta
          // interattiva e si possono usare in contemporanea la chat e il
          // documento aperto. La modale è comunque trascinabile (CdkDrag).
          hasBackdrop: false,
          // Senza backdrop il click fuori non chiude la modale; la si chiude
          // solo dal pulsante X (mat-dialog-close funziona anche con disableClose).
          disableClose: true,
        });
      },
      error: (err) => {
      }
    });
    return of({});
  }

  downloadFile(data: ExplorerEntity): Observable<any> {
    return this.fileManagerService.getFile(data.id).pipe(
      switchMap((dataResponse: ApiResponse<ExplorerEntity>) => {
        let fileData = dataResponse.data as ExplorerEntity;
        if (!fileData || !fileData.decrypted_file) {
          return of(null);
        }
        const fileName = fileData.name;
        const extension = fileName.includes('.') ? '' : this.getFileExtensionFromMimeType(fileData["content-type"] as string);
        const fullFileName = fileName + extension;
        const base64Data = fileData.decrypted_file;
        const byteCharacters = atob(base64Data.split(',')[1]);
        const byteArrays = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArrays[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteArrays], {type: fileData["content-type"]});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fullFileName;
        link.click();
        return of(null);
      })
    );
  }

  uploadFiles(parent: ExplorerEntity, files: FileList): Observable<any> {
    let file_error = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!environment.file_accepted_files.includes(file.type)) {
        file_error.push(file.name);
      }
    }
    if (file_error.length == 0) {
      const uploads: Observable<any>[] = [];
      let parent_id = null;
      if (Object.keys(parent).length !== 0) {
        parent_id = parent.id;
      }
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const upload$ = this.convertToBase64(file).pipe(
          switchMap((base64: string) => {
            const fileEntity: ExplorerEntity = {
              ...parent,
              name: file.name,
              field_name: file.name,
              type: 'file',
              parent: parent_id as number,
              file_base64: base64
            };
            return this.fileManagerService.uploadFile(fileEntity);
          })
        );
        uploads.push(upload$);
      }
      return forkJoin(uploads);
    } else {
      this.matDialog.open(ModalsDialogComponent, {
        data: {
          title: 'Error File Upload',
          description: 'The following files are invalid:',
          list_file: file_error,
          footer_text: 'The supported formats are: <br/>' + environment.file_accepted_files.join(', '),
        } as ModalData,
      });
      return of(null);
    }
  }

  createDir(parent: ExplorerEntity, name: string): Observable<any> {
    let parent_id = null;
    if (Object.keys(parent).length !== 0) {
      parent_id = parent.id as number;
    }
    const newFolder = {
      name: name,
      parent: parent_id,
      field_name: "",
      type: "folder",
    };
    return this.fileManagerService.createFolder(newFolder);
  }

  delete(data: ExplorerEntity[]): Observable<any> {
    const deleteCalls = data.map((item) => {
      return this.fileManagerService.deleteItem(item.id).pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          return of(null);
        })
      );
    });
    return forkJoin(deleteCalls);
  }

  getContent(data: ExplorerEntity): Observable<{ files: ExplorerEntity[], dirs: ExplorerEntity[] }> {
    let parent = null;
    if (Object.keys(data).length !== 0) {
      parent = data.id;
    }
    this.loadingService.show();
    return this.fileManagerService.getList(parent).pipe(
      takeUntil(this.destroy$),
      map((response) => {
        const dirs: ExplorerEntity[] = [];
        const files: ExplorerEntity[] = [];

        const items = response?.data;
        if (!items) {
          return {dirs: [], files: []};
        }
        items.forEach((item) => {
          if (item.type === 'folder') {
            dirs.push(item);
          } else if (item.type === 'file') {
            files.push(item);
          }
        });
        this.loadingService.hide();
        return {dirs, files};
      }),
      catchError((error) => {
        return of({dirs: [], files: []}); // fallback in caso di errore
      })
    );
  }

  rename(data: ExplorerEntity, newName: string): Observable<ExplorerEntity> {
    return this.fileManagerService.renameItem(data.id, newName).pipe(
      takeUntil(this.destroy$),
      catchError((error) => {
        return of({...data, name: newName});
      })
    );
  }

  moveitem(data: ExplorerEntity, parentId: number): Observable<ExplorerEntity> {
    return this.fileManagerService.moveItem(data.id, parentId, data.name).pipe(
      takeUntil(this.destroy$),
      catchError((error) => {
        return of({...data, parent: parentId, name: data.name});
      })
    );
  }

  getName(data: ExplorerEntity) {
    return data.name;
  }

  openTree(data: ExplorerEntity): Observable<Array<DataNode<ExplorerEntity>>> {
    return of([]);
  }

  private getFileExtensionFromMimeType(mimeType: string): string {
    const mimeTypes: { [key: string]: string } = {
      'image/jpeg': '.jpeg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'application/pdf': '.pdf',
      'text/plain': '.txt',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    };
    return mimeTypes[mimeType] || '';
  }

  private convertToBase64(file: File): Observable<string> {
    const reader = new FileReader();

    return new Observable<string>((observer) => {
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };

      reader.onerror = (error) => {
        observer.error(error);
      };

      reader.readAsDataURL(file);
    });
  }
}
